'use client';

import { useMemo, useEffect } from 'react';
import { PrivyProvider } from '@privy-io/react-auth';
import { privyConfig } from '@/lib/privy-config';
import { useEditorStore } from '@/store/theme-editor-store';

const PRIVY_LIGHT_VARS: Record<string, string> = {
  '--privy-color-background': '#FFFFFF',
  '--privy-color-background-2': '#F1F2F9',
  '--privy-color-background-3': '#F8F9FC',
  '--privy-color-foreground': '#040217',
  '--privy-color-foreground-2': '#64668B',
  '--privy-color-foreground-3': '#9498B8',
  '--privy-color-foreground-4': '#E2E3F0',
};

const PRIVY_DARK_VARS: Record<string, string> = {
  '--privy-color-background': '#020713',
  '--privy-color-background-2': '#1A2230',
  '--privy-color-background-3': '#101724',
  '--privy-color-foreground': '#F8F8F8',
  '--privy-color-foreground-2': '#9BA2AE',
  '--privy-color-foreground-3': '#7B8491',
  '--privy-color-foreground-4': '#1F2937',
};

export function PrivyProviderWrapper({ children }: { children: React.ReactNode }) {
  const appId = privyConfig.appId?.trim()
  const currentMode = useEditorStore(s => s.themeState.currentMode)

  useEffect(() => {
    const vars = currentMode === 'dark' ? PRIVY_DARK_VARS : PRIVY_LIGHT_VARS;
    for (const [key, value] of Object.entries(vars)) {
      document.body.style.setProperty(key, value);
    }
    return () => {
      for (const key of Object.keys(vars)) {
        document.body.style.removeProperty(key);
      }
    };
  }, [currentMode]);

  const config = useMemo(() => ({
    ...privyConfig.config,
    appearance: {
      ...privyConfig.config.appearance,
      theme: currentMode as 'light' | 'dark',
    },
  }), [currentMode])

  if (!appId) return <>{children}</>;
  return (
    <PrivyProvider appId={appId} config={config}>
      {children}
    </PrivyProvider>
  );
}
